import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  cardEmbedSchema,
  cardExportSchema,
  cardMergeBodySchema,
  cardMergeResultSchema,
  type CardEmbed,
  type CardExport,
  type CardMergeResult,
} from '@molt/shared';
import { announce } from './announce';
import { get, post } from './api';
import { useInterfaceLanguage } from './interface-language';
import { queryKeys } from './query-keys';

export interface MergeCardsVariables {
  readonly targetCardId: string;
  readonly sourceCardId: string;
  readonly targetTitle: string;
  readonly sourceTitle: string;
}

export function mergeCards(
  targetCardId: string,
  sourceCardId: string,
): Promise<CardMergeResult> {
  return post(
    `/cards/${encodeURIComponent(targetCardId)}/merge`,
    cardMergeResultSchema,
    cardMergeBodySchema.parse({ sourceCardId }),
  );
}

export function exportCard(cardId: string): Promise<CardExport> {
  return get(`/cards/${encodeURIComponent(cardId)}/export`, cardExportSchema);
}

export function embedCard(cardId: string): Promise<CardEmbed> {
  return get(`/cards/${encodeURIComponent(cardId)}/embed`, cardEmbedSchema);
}

export function cardExportQueryOptions(cardId: string) {
  return queryOptions({
    queryKey: queryKeys.cards.export(cardId),
    queryFn: () => exportCard(cardId),
  });
}

export function cardEmbedQueryOptions(cardId: string) {
  return queryOptions({
    queryKey: queryKeys.cards.embed(cardId),
    queryFn: () => embedCard(cardId),
  });
}

export function cardEmbedUrl(cardId: string, origin: string): string {
  return new URL(
    `/embed/cards/${encodeURIComponent(cardId)}`,
    `${new URL(origin).origin}/`,
  ).toString();
}

export function useMergeCards(
  boardId: string,
): UseMutationResult<CardMergeResult, unknown, MergeCardsVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables) => mergeCards(variables.targetCardId, variables.sourceCardId),
    onSuccess: (result, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(result.card.id), result.card);
      for (const source of result.sourceCards) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.cards.detail(source.id),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.targetCardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(variables.sourceCardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.archiveAll(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.activity(boardId, 'all'),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.activity(result.card.id, 'all'),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
    },
    onError: (_error, variables) => {
      announce(
        t('primitives.cardShare.mergeFailed', {
          target: variables.targetTitle,
          source: variables.sourceTitle,
        }),
      );
    },
  });
}
